import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subscription } from 'rxjs';
import { AzTabsComponent, AzTab } from '../../../../shared/components/az-tabs/az-tabs.component';
import { LegalEntityInformationComponent } from '../legal-entity-information/legal-entity-information.component';
import { LegalEntityDocumentsComponent } from '../legal-entity-documents/legal-entity-documents.component';
import { ScrollActivityDirective } from '../../../../shared/directives/scroll-activity.directive';
import { LegalEntityService } from '../../services/legal-entity.service';
import { SelectedLegalEntityService } from '../../../../shared/services/selected-legal-entity.service';

@Component({
  selector: 'app-legal-entity-detail',
  imports: [
    AzTabsComponent,
    LegalEntityInformationComponent,
    LegalEntityDocumentsComponent,
    ScrollActivityDirective,
  ],
  templateUrl: './legal-entity-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './legal-entity-detail.component.scss',
})
export class LegalEntityDetailComponent implements OnChanges, OnDestroy {
  @Input() entityId: number | null = null;

  tabs: AzTab[] = [
    { id: 'info', label: 'Legal Entity Information' },
    { id: 'documents', label: 'Documents' },
  ];

  private statusSubscription?: Subscription;
  private reloadSubscription?: Subscription;

  get activeTabId() {
    return this.selectedLegalEntity.activeTabId;
  }

  constructor(
    private legalEntityService: LegalEntityService,
    private selectedLegalEntity: SelectedLegalEntityService,
  ) {
    // Subscribe to reload requests from other components (e.g., toolbar after status change)
    this.reloadSubscription = this.selectedLegalEntity.reloadEntityDetail$.subscribe(() => {
      this.loadEntityStatus();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityId'] && this.entityId) {
      this.loadEntityStatus();
    }
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.reloadSubscription?.unsubscribe();
  }

  loadEntityStatus(): void {
    if (!this.entityId) return;

    this.statusSubscription?.unsubscribe();
    this.selectedLegalEntity.setStatusLoading(true);

    this.statusSubscription = this.legalEntityService
      .getLegalEntityDetail(this.entityId)
      .subscribe({
        next: (detail) => {
          const status = detail.bcr_status?.status_name || '';
          const bcrStatusId = detail.bcr_status?.id || null;
          this.selectedLegalEntity.setStatus(status);
          this.selectedLegalEntity.setBcrStatusId(bcrStatusId);
          this.selectedLegalEntity.setEntityDetail(detail);
          this.selectedLegalEntity.setStatusLoading(false);
        },
        error: () => {
          this.selectedLegalEntity.setStatus('');
          this.selectedLegalEntity.setBcrStatusId(null);
          this.selectedLegalEntity.setStatusLoading(false);
        },
      });
  }

  onTabChange(tabId: string): void {
    this.selectedLegalEntity.setActiveTabId(tabId);

    // Reload data when switching to Legal Entity Information tab
    if (tabId === 'info' && this.entityId) {
      this.loadEntityStatus();
    }
  }
}
